/** biome-ignore-all lint/complexity/noStaticOnlyClass: Utility class for CAIP parsing */
import { CAIPParseError, CAIPValidationError } from "./errors";
import type { ParsedAccountId, ParsedAssetId, ParsedChainId } from "./types";
import { Validator } from "./validator";

/**
 * CAIP Parser
 * Provides static parsing methods for CAIP identifiers
 */
export class Parser {
  /**
   * Parses a Chain ID string into its components
   * @throws {CAIPParseError} if the chain ID is invalid
   */
  static parseChainId(chainId: string): ParsedChainId {
    if (!Validator.isValidChainId(chainId)) {
      throw new CAIPParseError(
        "Invalid Chain ID format. Expected format: {namespace}:{reference}",
        chainId,
      );
    }

    const parts = chainId.split(":");
    const chainNamespace = parts[0];
    const chainReference = parts[1];

    if (!chainNamespace || !Validator.isValidChainNamespace(chainNamespace)) {
      throw new CAIPValidationError(
        `Invalid chain namespace: ${chainNamespace}. Must be one of: eip155, solana, cosmos, starknet, sui`,
        chainNamespace || "",
      );
    }

    if (!chainReference || !Validator.isValidChainReference(chainReference)) {
      throw new CAIPValidationError(
        `Invalid chain reference: ${chainReference}. Must match pattern: [-_a-zA-Z0-9]{1,32}`,
        chainReference || "",
      );
    }

    return { chain: { namespace: chainNamespace, reference: chainReference } };
  }

  /**
   * Parses an Account ID string into its components
   * @throws {CAIPParseError} if the account ID is invalid
   */
  static parseAccountId(accountId: string): ParsedAccountId {
    if (!Validator.isValidAccountId(accountId)) {
      throw new CAIPParseError(
        "Invalid Account ID format. Expected format: {chain_id}:{account_address}",
        accountId,
      );
    }

    const parts = accountId.split(":");
    if (parts.length !== 3) {
      throw new CAIPParseError(
        "Invalid Account ID format. Expected 3 parts separated by ':'",
        accountId,
      );
    }

    const chainNamespace = parts[0];
    const chainReference = parts[1];
    const accountAddress = parts[2];

    if (!chainNamespace || !Validator.isValidChainNamespace(chainNamespace)) {
      throw new CAIPValidationError(
        `Invalid chain namespace: ${chainNamespace}. Must be one of: eip155, solana, cosmos, starknet, sui`,
        chainNamespace || "",
      );
    }

    if (!chainReference || !Validator.isValidChainReference(chainReference)) {
      throw new CAIPValidationError(
        `Invalid chain reference: ${chainReference}. Must match pattern: [-_a-zA-Z0-9]{1,32}`,
        chainReference || "",
      );
    }

    if (!accountAddress || !Validator.isValidAccountAddress(accountAddress)) {
      throw new CAIPValidationError(
        `Invalid account address: ${accountAddress}. Must match pattern: [-.%a-zA-Z0-9]{1,128}`,
        accountAddress || "",
      );
    }

    return {
      chain: { namespace: chainNamespace, reference: chainReference },
      account: { address: accountAddress },
    };
  }

  /**
   * Parses an Asset ID string into its components
   * @throws {CAIPParseError} if the asset ID is invalid
   */
  static parseAssetId(assetId: string): ParsedAssetId {
    if (!Validator.isValidAssetId(assetId)) {
      throw new CAIPParseError(
        "Invalid Asset ID format. Expected format: {chain_id}/{asset_namespace}:{asset_reference}",
        assetId,
      );
    }

    const parts = assetId.split("/");
    const chainPart = parts[0];
    const assetPart = parts[1];

    if (!chainPart || !assetPart) {
      throw new CAIPParseError(
        "Invalid Asset ID format. Must contain '/' separator",
        assetId,
      );
    }

    const chainParts = chainPart.split(":");
    const chainNamespace = chainParts[0];
    const chainReference = chainParts[1];

    const assetNamespaceSeparatorIndex = assetPart.indexOf(":");
    const assetNamespace = assetPart.slice(0, assetNamespaceSeparatorIndex);
    const assetReference = assetPart.slice(assetNamespaceSeparatorIndex + 1);

    if (!chainNamespace || !Validator.isValidChainNamespace(chainNamespace)) {
      throw new CAIPValidationError(
        `Invalid chain namespace: ${chainNamespace}. Must be one of: eip155, solana, cosmos, starknet, sui`,
        chainNamespace || "",
      );
    }

    if (!chainReference || !Validator.isValidChainReference(chainReference)) {
      throw new CAIPValidationError(
        `Invalid chain reference: ${chainReference}`,
        chainReference || "",
      );
    }

    if (!assetNamespace || !Validator.isValidAssetNamespace(assetNamespace)) {
      throw new CAIPValidationError(
        `Invalid asset namespace: ${assetNamespace}. Must be one of: erc20, slip44, spl, ibc, erc721, erc1155, coin`,
        assetNamespace || "",
      );
    }

    if (
      !assetReference ||
      !Validator.isValidAssetReference(assetReference, assetNamespace)
    ) {
      throw new CAIPValidationError(
        `Invalid asset reference: ${assetReference}`,
        assetReference || "",
      );
    }

    return {
      chain: { namespace: chainNamespace, reference: chainReference },
      asset: { namespace: assetNamespace, reference: assetReference },
    };
  }
}
